import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    User, MapPin, Clock, Calendar, 
    MoreHorizontal, Search, Trash2, 
    ChevronRight, Loader2, UserCheck, 
    Activity, ClipboardList
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PatientLocationView = ({ type = 'outpatient', title = 'OPD Patients' }) => {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchLocationPatients();
        const interval = setInterval(fetchLocationPatients, 30000); // Polling every 30s
        return () => clearInterval(interval);
    }, [type]);

    const fetchLocationPatients = async () => {
        try {
            // Fetch check-ins for this specific location
            const response = await axios.get(`http://localhost:5000/api/checkin?type=${type}`, { headers });
            setPatients(response.data);
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredPatients = (Array.isArray(patients) ? patients : []).filter(p => {
        const patientData = p.Patient || p.patient;
        const firstName = patientData?.firstName || '';
        const lastName = patientData?.lastName || '';
        const ticket = p.ticketNumber || '';
        
        return firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               ticket.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div className="p-4 md:p-8 bg-[#F8FAFC] min-h-screen">
            <div className="max-w-7xl mx-auto space-y-8">
                
                {/* Location Header */}
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-white p-8 rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/30 rounded-full -mr-20 -mt-20 blur-3xl" />
                    <div className="relative z-10 flex items-center gap-5">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                            type === 'outpatient' ? 'bg-indigo-600' :
                            type === 'emergency' ? 'bg-rose-600' :
                            type === 'inpatient' ? 'bg-emerald-600' : 'bg-slate-800'
                        }`}>
                            <MapPin className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-slate-800 tracking-tight">{title}</h1>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Live Location Occupancy Console</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 relative z-10 w-full md:w-auto">
                        <div className="relative flex-grow md:min-w-[300px]">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 border-none outline-none" />
                            <input 
                                type="text"
                                placeholder="Search by Ticket or Name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-14 bg-slate-50 border border-slate-100 rounded-2xl pl-14 pr-6 text-xs font-black outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 transition-all"
                            />
                        </div>
                        <button className="h-14 w-14 bg-white border border-slate-100 rounded-2xl flex items-center justify-center hover:bg-slate-50 transition-all font-black text-slate-400">
                             <MoreHorizontal className="w-5 h-5" />
                        </button>
                    </div>
                </header>

                {loading ? (
                    <div className="py-40 flex flex-col items-center justify-center gap-4 opacity-50">
                        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
                        <p className="text-[10px] font-black uppercase tracking-widest">Scanning Location...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
                        {filteredPatients.map((p) => (
                            <div key={p.id} className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-xl shadow-slate-200/20 hover:shadow-2xl transition-all group relative overflow-hidden">
                                <div className={`absolute top-0 left-0 w-2 h-full ${
                                    type === 'outpatient' ? 'bg-indigo-500' :
                                    type === 'emergency' ? 'bg-rose-500' : 'bg-emerald-500'
                                }`} />
                                
                                <div className="flex justify-between items-start mb-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center relative shadow-sm border border-slate-100">
                                            <User className="w-6 h-6 text-slate-400" />
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white animate-pulse" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-black text-slate-800 tracking-tight leading-tight">
                                                {((p.Patient || p.patient)?.firstName || 'Unknown')} {((p.Patient || p.patient)?.lastName || '')}
                                            </h3>
                                            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mt-1">Ticket: {p.ticketNumber}</p>
                                        </div>
                                    </div>
                                    <button className="p-3 bg-slate-50 text-slate-400 rounded-xl hover:bg-rose-50 hover:text-rose-400 transition-all opacity-0 group-hover:opacity-100 shadow-sm border border-slate-100"><Trash2 className="w-4 h-4" /></button>
                                </div>

                                <div className="grid grid-cols-2 gap-4 border-t border-slate-50 pt-6">
                                    <div className="space-y-1">
                                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-300">Checked-in At</label>
                                        <div className="flex items-center gap-2 text-slate-700 font-extrabold text-xs">
                                            <Clock className="w-3.5 h-3.5 text-indigo-400" />
                                            {formatDate(p.createdAt)}
                                        </div>
                                    </div>
                                    <div className="space-y-1 text-right">
                                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-300">Handled By</label>
                                        <div className="flex items-center justify-end gap-2 text-slate-600 font-bold text-[10px] uppercase">
                                            <UserCheck className="w-3.5 h-3.5 text-slate-300" />
                                            Admin Staff
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 flex gap-3 pt-6 border-t border-slate-50/50">
                                    <button className="flex-1 py-3.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all">Move</button>
                                    <button className={`flex-1 py-3.5 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all hover:bg-black active:scale-95 flex items-center justify-center gap-2 ${
                                        type === 'outpatient' ? 'bg-indigo-600 shadow-indigo-100' :
                                        type === 'emergency' ? 'bg-rose-600 shadow-rose-100' : 'bg-emerald-600 shadow-emerald-100'
                                    }`}>
                                        Actions <ChevronRight className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {patients.length === 0 && !loading && (
                    <div className="py-40 text-center bg-white/50 border border-dashed border-slate-200 rounded-[56px] space-y-6">
                        <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm">
                            <Activity className="w-10 h-10 text-slate-200" />
                        </div>
                        <p className="text-sm font-black text-slate-300 uppercase tracking-widest italic">No active patients in this location.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientLocationView;
